import React from 'react'
import { FaGoogle, FaDiscord, FaMicrosoft, FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { useAuth } from '../contexts/AuthContext'

const AccountInfo = () => {
    const { user: currentUser } = useAuth()

    if (!currentUser) return null

    // Map provider names to icons and display names
    const providerInfo = {
        google: {
            icon: <FaGoogle className="text-xl" />,
            name: 'Google',
            color: 'text-red-400'
        },
        discord: {
            icon: <FaDiscord className="text-xl" />,
            name: 'Discord',
            color: 'text-indigo-400'
        },
        microsoft: {
            icon: <FaMicrosoft className="text-xl" />,
            name: 'Microsoft',
            color: 'text-blue-400'
        },
        local: {
            icon: <span className="text-xl">🔐</span>,
            name: 'Email/Password',
            color: 'text-gray-400'
        }
    }

    // Get list of linked providers
    const linkedProviders = currentUser.authProviders?.map(ap => ap.provider) || []
    const allProviders = ['google', 'discord', 'microsoft', 'local']

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        })
    }

    return (
        <div className="space-y-6">
            {/* Account Details */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-6">
                <h3 className="text-lg font-semibold mb-4" style={{color: 'var(--theme-text-primary)'}}>
                    Account Information
                </h3>

                <div className="space-y-4">
                    {/* Email */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm" style={{color: 'var(--theme-text-secondary)'}}>
                            Email
                        </span>
                        <span className="text-sm font-medium" style={{color: 'var(--theme-text-primary)'}}>
                            {currentUser.email}
                        </span>
                    </div>

                    {/* Username */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm" style={{color: 'var(--theme-text-secondary)'}}>
                            Username
                        </span>
                        <span className="text-sm font-medium" style={{color: 'var(--theme-text-primary)'}}>
                            {currentUser.username}
                        </span>
                    </div>

                    {/* Account Created */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm" style={{color: 'var(--theme-text-secondary)'}}>
                            Member Since
                        </span>
                        <span className="text-sm font-medium" style={{color: 'var(--theme-text-primary)'}}>
                            {formatDate(currentUser.createdAt)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Linked Accounts */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-6">
                <h3 className="text-lg font-semibold mb-4" style={{color: 'var(--theme-text-primary)'}}>
                    Linked Accounts
                </h3>
                <p className="text-xs mb-4" style={{color: 'var(--theme-text-muted)'}}>
                    These login methods are connected to your account
                </p>

                <div className="space-y-3">
                    {allProviders.map(provider => {
                        const info = providerInfo[provider]
                        const isLinked = linkedProviders.includes(provider)

                        return (
                            <div
                                key={provider}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                    isLinked
                                        ? 'bg-white/5 border-white/20'
                                        : 'bg-transparent border-white/5 opacity-50'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={info.color}>
                                        {info.icon}
                                    </div>
                                    <span className="text-sm font-medium" style={{color: 'var(--theme-text-primary)'}}>
                                        {info.name}
                                    </span>
                                </div>

                                {isLinked ? (
                                    <div className="flex items-center gap-2">
                                        <FaCheckCircle className="text-green-400 text-sm" />
                                        <span className="text-xs text-green-400">Connected</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <FaTimesCircle className="text-gray-500 text-sm" />
                                        <span className="text-xs text-gray-500">Not connected</span>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* Info message */}
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <p className="text-xs" style={{color: 'var(--theme-text-secondary)'}}>
                        💡 You can use any linked login method to sign in to your account
                    </p>
                </div>
            </div>
        </div>
    )
}

export default AccountInfo
